import React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import BigCTA from "../components/BigCTA";
import { SERVICES } from "../data/content";

const ServiceVisual = ({ num, alt }) => {
  // Alternating geometric SVG decorations
  const variants = [
    (color) => (
      <g stroke={color} strokeWidth="1.2" fill="none">
        {Array.from({ length: 8 }).map((_, i) => (
          <circle key={i} cx={150 + i * 4} cy={150 + i * 4} r={130 - i * 8} />
        ))}
      </g>
    ),
    (color) => (
      <g stroke={color} strokeWidth="1.2" fill="none">
        {Array.from({ length: 14 }).map((_, i) => (
          <line key={i} x1={i * 22} y1="0" x2={i * 22 - 60} y2="300" />
        ))}
        <rect x="40" y="60" width="220" height="180" />
      </g>
    ),
    (color) => (
      <g stroke={color} strokeWidth="1.2" fill="none">
        <path d="M0,250 Q150,50 300,250" />
        <path d="M0,200 Q150,0 300,200" />
        <path d="M0,150 Q150,-50 300,150" />
        <circle cx="150" cy="150" r="60" />
      </g>
    ),
    (color) => (
      <g stroke={color} strokeWidth="1.2" fill="none">
        {Array.from({ length: 8 }).map((_, i) =>
          Array.from({ length: 8 }).map((__, j) => (
            <rect key={`${i}-${j}`} x={20 + i * 35} y={20 + j * 35} width="20" height="20" />
          ))
        )}
      </g>
    ),
    (color) => (
      <g stroke={color} strokeWidth="1.2" fill="none">
        <polygon points="150,30 270,260 30,260" />
        <polygon points="150,90 240,250 60,250" />
        <polygon points="150,150 210,240 90,240" />
      </g>
    ),
    (color) => (
      <g stroke={color} strokeWidth="1.2" fill="none">
        {Array.from({ length: 5 }).map((_, i) => (
          <path
            key={i}
            d={`M0,${50 + i * 50} Q150,${i % 2 === 0 ? 0 : 300} 300,${50 + i * 50}`}
          />
        ))}
      </g>
    ),
  ];
  const idx = (parseInt(num, 10) - 1) % variants.length;
  return (
    <svg viewBox="0 0 300 300" className="w-full h-full" aria-label={alt}>
      {variants[idx]("currentColor")}
    </svg>
  );
};

const Servizi = () => {
  return (
    <>
      <Seo
        title="Servizi: strategia, sponsorship, content, performance | OnSports"
        description="I 6 servizi OnSports per brand sportivi: Impact Strategy, Sponsorship Programs, Content & IP Studio, PitchPartner, Research & Insight, Performance Marketing."
        path="/servizi"
      />

      {/* HERO */}
      <section className="bg-dodgers text-galactico pt-40 pb-24 md:pt-52 md:pb-32" data-testid="servizi-hero">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Servizi</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <h1 className="display-h1 mt-8 max-w-[20ch]">
              Servizi integrati per <span className="text-lime">impatto misurabile</span> nella sport industry.
            </h1>
          </RevealOnScroll>
          <RevealOnScroll delay={0.2}>
            <p className="mt-9 body-lg opacity-85 max-w-[60ch]">
              Non vendiamo servizi isolati. Ogni area si connette alle altre per creare
              sistemi di impatto coerenti.
            </p>
          </RevealOnScroll>
        </div>
      </section>

      {SERVICES.map((s, idx) => {
        const isDark = idx % 2 === 0; // alternate
        const bg = isDark ? "bg-dodgers text-galactico" : "bg-galactico text-dodgers";
        return (
          <section
            key={s.slug}
            className={`section-pad ${bg} relative overflow-hidden`}
            data-testid={`service-section-${s.num}`}
          >
            <div className="container-os">
              <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
                <div className="col-span-12 md:col-span-8">
                  <SectionEyebrow num={s.num}>{s.title}</SectionEyebrow>
                  <h2 className="display-h2 mt-5 max-w-[18ch]">{s.headline}</h2>
                  <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed opacity-90 max-w-[60ch]">
                    {s.body}
                  </p>
                  <ul className="mt-9 space-y-4">
                    {s.items.map((it, i) => (
                      <li key={i} className="flex items-start gap-4">
                        <span className="mt-1 flex-shrink-0 w-6 h-6 bg-lime text-dodgers flex items-center justify-center">
                          <Check size={14} strokeWidth={3} />
                        </span>
                        <span className="text-[1rem] md:text-[1.0625rem] leading-relaxed">{it}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-10">
                    <Link to={`/servizi/${s.slug}`} data-testid={`service-cta-${s.num}`}>
                      <Button variant={isDark ? "primary" : "secondary-dark"}>{s.cta}</Button>
                    </Link>
                  </div>
                </div>
                <div className="hidden md:flex col-span-4 items-center justify-center aspect-square">
                  <div className={`w-full ${isDark ? "text-lime" : "text-dodgers"} opacity-70`}>
                    <ServiceVisual num={s.num} alt={s.title} />
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA FINALE */}
      <section className="bg-dodgers text-galactico py-24 md:py-40" data-testid="servizi-cta-finale">
        <div className="container-os">
          <RevealOnScroll>
            <h2 className="display-h2 max-w-[16ch]">Non sai da dove partire?</h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <p className="mt-8 body-lg opacity-85 max-w-[60ch]">
              Un servizio non basta mai da solo. Costruiamo con te il mix giusto per i tuoi obiettivi.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <Link to="/contatti" className="mt-10 inline-block" data-testid="servizi-cta-scoping">
              <Button variant="primary">Prenota una call di scoping</Button>
            </Link>
          </RevealOnScroll>
        </div>
      </section>

      <BigCTA />
    </>
  );
};

export default Servizi;
